import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaLock, FaEye, FaEyeSlash, FaEnvelope, FaPhone, FaExclamationTriangle } from 'react-icons/fa';
import { authAPI } from '../../api';
import FeeStructure from '../FeeStructure/FeeStructure';
import './Registration.css';

const Registration = () => {
  const [formData, setFormData] = useState({
    title: '',
    lastName: '',
    nameInitials: '',
    username: '',
    email: '',
    phone: '',
    affiliation: '',
    password: '',
    confirmPassword: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const titles = ['Prof', 'Dr', 'Mr', 'Mrs', 'Ms'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    if (error) setError('');
  };

  const validateForm = () => {
    if (!formData.title) return 'Please select a title';
    if (!formData.lastName.trim()) return 'Last name is required';
    if (!formData.nameInitials.trim()) return 'Name initials are required';
    if (!formData.username.trim()) return 'Username is required';
    if (!formData.email.trim()) return 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return 'Please enter a valid email';
    if (!formData.phone.trim()) return 'Phone number is required';
    if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) return 'Please enter a valid 10-digit phone number';
    if (!formData.affiliation.trim()) return 'Affiliation is required';
    if (!formData.password) return 'Password is required';
    if (formData.password.length < 6) return 'Password must be at least 6 characters';
    if (formData.password !== formData.confirmPassword) return 'Passwords do not match';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);
    
    // Call backend API
    const result = await authAPI.register(formData);
    setIsLoading(false);

    if (result.success) {
      navigate('/registration-success');
    } else {
      setError(result.message || 'Registration failed. Please try again.');
    }
  };

  return (
    <div className="registration-page">
      {/* Fee Structure Section */}
      <FeeStructure />
      
      <div className="registration-container">
        <div className="registration-card">
          <h1 className="registration-title">Create Your Account</h1>
          <p className="registration-subtitle">Join us for the IIITDM Conference 2026</p>

          {error && (
            <div className="error-message">
              <FaExclamationTriangle className="error-icon" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="registration-form">
            {/* Title */}
            <div className="registration-form-group">
              <label htmlFor="title" className="registration-form-label">
                <FaUser /> Title
              </label>
              <select
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="registration-form-control"
              >
                <option value="">Select Title</option>
                {titles.map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>

            {/* Name Initials */}
            <div className="registration-form-group">
              <label htmlFor="nameInitials" className="registration-form-label">
                <FaUser /> First Name
              </label>
              <input
                type="text"
                id="nameInitials"
                name="nameInitials"
                value={formData.nameInitials}
                onChange={handleChange}
                className="registration-form-control"
                placeholder="e.g., A.B.C"
                maxLength="10"
              />
            </div>

            {/* Last Name */}
            <div className="registration-form-group">
              <label htmlFor="lastName" className="registration-form-label">
                <FaUser /> Last Name
              </label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                className="registration-form-control"
                placeholder="Enter your last name"
              />
            </div>

            {/* Affiliation */}
            <div className="registration-form-group">
              <label htmlFor="affiliation" className="registration-form-label">
                <FaUser /> Affiliation
              </label>
              <input
                type="text"
                id="affiliation"
                name="affiliation"
                value={formData.affiliation}
                onChange={handleChange}
                className="registration-form-control"
                placeholder="e.g., University/Organization name"
              />
            </div>

            {/* Email */}
            <div className="registration-form-group">
              <label htmlFor="email" className="registration-form-label">
                <FaEnvelope /> Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="registration-form-control"
                placeholder="Enter your email"
              />
            </div>

            {/* Phone */}
            <div className="registration-form-group">
              <label htmlFor="phone" className="registration-form-label">
                <FaPhone /> Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="registration-form-control"
                placeholder="Enter 10-digit phone number"
              />
            </div>

            {/* Username */}
            <div className="registration-form-group">
              <label htmlFor="username" className="registration-form-label">
                <FaUser /> Username
              </label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                className="registration-form-control"
                placeholder="Create a username"
              />
            </div>

            {/* Password */}
            <div className="registration-form-group">
              <label htmlFor="password" className="registration-form-label">
                <FaLock /> Password
              </label>
              <div className="registration-password-input-group">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="registration-form-control"
                  placeholder="Enter password"
                />
                <button
                  type="button"
                  className="registration-password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="registration-form-group">
              <label htmlFor="confirmPassword" className="registration-form-label">
                <FaLock /> Confirm Password
              </label>
              <div className="registration-password-input-group">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="registration-form-control"
                  placeholder="Confirm password"
                />
                <button
                  type="button"
                  className="registration-password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="registration-btn registration-btn-primary"
              disabled={isLoading}
            >
              {isLoading ? 'Creating Account...' : 'Register'}
            </button>
          </form>

          <div className="registration-login-link">
            Already have an account? <a href="/login">Login here</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Registration;
